import React, { useEffect, useState } from 'react';
import { Box, ShowIcon, Table } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { currencyCommaConvert } from '@epi-helpers/numeral';
import { TopupReportsItemsTableProps } from '@epi-models/pages/Reports';

import { StatusDot } from '../../TopupReports.styles';
import { reportStatusesColors } from '../../../utils';
import { usePersonalIdMasking } from '@epi-shared/hooks/usePersonalIdMasking';

const today = new Date();

export const TopupReportsItemsTable = ({
  visibleReportItems,
  scheduledOn,
  benefitType
}: TopupReportsItemsTableProps) => {
  const isTypeLunch = benefitType === 'Lunch';
  const [columnSize, setColumnSize] = useState(['']);
  const { t } = useTranslation();
  const getStatus = (status: string) => {
    if (scheduledOn && status === 'top_up_request_status.scheduled') {
      return new Date(scheduledOn) > today
        ? 'top_up_request_status.waiting_for_load_date'
        : 'top_up_request_status.payment_not_received';
    }
    return status;
  };

  useEffect(() => {
    if (isTypeLunch) {
      setColumnSize(['20%', '19%', '10%', '10%', '5%', '16%', '10%']);
    } else {
      setColumnSize(['25%', '25%', '0%', '0%', '0%', '17%', '20%']);
    }
  }, [isTypeLunch]);

  const { handleClick, isError, isLoading, unmaskedDetails } =
    usePersonalIdMasking();

  return (
    <Table id="topup-reports-single-table">
      <Table.Head>
        <Table.Row>
          <Table.Cell
            width={columnSize[0]}
            id="topup-reports-single-table-name"
          >
            {t('containers.deposit_money.reports_tab.name')}
          </Table.Cell>
          <Table.Cell
            width={columnSize[1]}
            id="topup-reports-single-table-personal_id"
          >
            {t('containers.deposit_money.reports_tab.personal_id')}
          </Table.Cell>
          {isTypeLunch && (
            <>
              <Table.Cell
                width={columnSize[2]}
                id="topup-reports-single-table-loaded_for"
              >
                {t('containers.deposit_money.reports_tab.loaded_for')}
              </Table.Cell>
              <Table.Cell
                width={columnSize[3]}
                id="topup-reports-single-table-load_value"
                align="right"
              >
                {t('containers.deposit_money.reports_tab.load_value')}
              </Table.Cell>
              <Table.Cell
                width={columnSize[4]}
                id="topup-reports-space"
              ></Table.Cell>
            </>
          )}
          <Table.Cell
            width={columnSize[5]}
            id="topup-reports-single-table-status"
          >
            {t('containers.deposit_money.reports_tab.status')}
          </Table.Cell>
          <Table.Cell
            width={columnSize[6]}
            align="right"
            id="topup-reports-single-table-load"
          >
            {t('containers.deposit_money.reports_tab.total_load')}
          </Table.Cell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {visibleReportItems?.map((item, i) => (
          <Table.Row key={item.personId + i} height="51px">
            <Table.Cell
              width={columnSize[0]}
              id="topup-reports-single-table-cell-name"
            >
              {item.firstName} {item.lastName}
            </Table.Cell>
            <Table.Cell
              id="topup-reports-single-table-cell-personal_id"
              sx={{ cursor: 'pointer', witdh: columnSize[1] }}
              onClick={() => handleClick(item?.personId)}
            >
              {!isLoading &&
              !isError &&
              unmaskedDetails?.personId === item?.personId ? (
                unmaskedDetails?.personalId
              ) : (
                <Box ml={'30px'}>
                  <ShowIcon />
                </Box>
              )}
            </Table.Cell>
            {isTypeLunch && (
              <>
                <Table.Cell
                  width={columnSize[2]}
                  id="topup-reports-single-table-cell-loaded_for"
                >
                  {item.numberOfWorkingDays}{' '}
                  {item.numberOfWorkingDays === 1
                    ? t('containers.deposit_money.reports_tab.day')
                    : t('containers.deposit_money.reports_tab.days')}
                </Table.Cell>
                <Table.Cell
                  width={columnSize[3]}
                  id="topup-reports-single-table-cell-load_value"
                  align="right"
                >
                  {currencyCommaConvert(item.dailyAmount)}
                </Table.Cell>
                <Table.Cell
                  width={columnSize[4]}
                  id="topup-reports-space"
                ></Table.Cell>
              </>
            )}
            <Table.Cell
              width={columnSize[5]}
              id="topup-reports-single-table-cell-status"
            >
              <Box display="flex" alignItems="center">
                <StatusDot
                  $dotColor={reportStatusesColors[getStatus(item.status)]}
                />
                {t(
                  `containers.deposit_money.reports_tab.statuses.${getStatus(
                    item.status
                  )}`
                )}
              </Box>
            </Table.Cell>
            <Table.Cell width={columnSize[6]} align="right">
              {currencyCommaConvert(item.amount)}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};
